import {
  adminGrants,
  AdminError,
  authClient,
  toAdminError,
  type AdminGrants,
} from '@/lib/auth';

/**
 * Staff and their grants over the auth Worker's admin API (`staff-holds-the-grants` D1):
 * `GET /admin/permissions` and `POST /admin/set-permissions`. The `/staff/*` routes this
 * once called were designed and never built, and answer 404.
 */

/** An admin and what their `adminGrant` row holds. No giver and no date: the row keeps neither (D12). */
export type StaffMember = AdminGrants;

/** The Worker's own `MANAGES_PERMISSIONS` — the one old-vocabulary name spelled here, and not a map (D5). */
export const MANAGES = 'superUsers';

/**
 * Whether a staff member is a Super User: holds `superUsers`, the grant that manages every
 * other. The Flutter admin shows User Permissions to its superUsers only (`avatar_menu.dart`),
 * so the menu row and the `/permissions` route both ask this. Convenience, not the guard: the
 * auth Worker refuses the reads and writes of anybody else itself (A4).
 */
export function isSuperUser(staff: { perms: readonly string[] }): boolean {
  return staff.perms.includes(MANAGES);
}

/** Everything this tool caches, so one invalidation settles a write's every reader. */
export const KEY = ['staff'] as const;

export function listStaff(): Promise<StaffMember[]> {
  return adminGrants();
}

/** The only write: `set-permissions` replaces the row's list with what it is sent (D2). */
export async function setPermissions(
  userId: string,
  permissions: readonly string[],
): Promise<void> {
  const { error } = await authClient.$fetch('/admin/set-permissions', {
    method: 'POST',
    body: { userId, permissions },
  });
  if (error) throw toAdminError(error);
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  const held = new Set(a);
  return a.length === b.length && b.every((entry) => held.has(entry));
}

/**
 * One permission given or taken away, as the whole list the Worker wants (D2). The base is
 * re-read rather than taken from the page, and a base that moved under it is refused rather
 * than written over (D3) — the Worker has no compare-and-set, so this is the only warning
 * either staff member gets. Whatever else the row holds rides along, known here or not (D4).
 */
export async function changeGrant(
  change: 'grant' | 'revoke',
  member: StaffMember,
  permission: string,
): Promise<void> {
  const fresh = (await listStaff()).find((admin) => admin.id === member.id);
  if (!fresh) {
    throw new Error(`${member.email} is no longer staff. Refresh the page.`);
  }
  if (!sameList(fresh.permissions, member.permissions)) {
    throw new Error(
      `${member.email}'s permissions changed while this page was open. Refresh and try again.`,
    );
  }
  const held = new Set(fresh.permissions);
  if (change === 'grant') held.add(permission);
  else held.delete(permission);
  await setPermissions(member.id, [...held]);
}

/** Take the role away; the Worker's after-hook deletes their row, so every permission goes with it (D9). */
export async function removeStaff(userId: string): Promise<void> {
  const { error } = await authClient.admin.setRole({ userId, role: 'user' });
  if (error) throw toAdminError(error);
}

/* Keyed on the Worker's machine code first (D6); `invalid` names the missing backend change, not the staff member. */
const SENTENCES: Record<string, string> = {
  last_manager: `Tutero would be left with nobody who can change permissions. Give ${MANAGES} to another admin first.`,
  not_admin:
    'That account is not staff, so it holds no permissions. Make them staff first.',
  invalid:
    'The auth Worker does not know that permission yet. Its list is still the old Firestore one, and the per-tool names have to be added to ADMIN_PERMISSIONS first.',
  email_taken: "Another admin's row already holds that address.",
};

const BY_STATUS: Record<number, string> = {
  0: 'The auth Worker could not be reached.',
  401: 'Your session has expired. Sign in again.',
  403: `Only an admin holding ${MANAGES} can change permissions.`,
  404: 'No account with that id.',
};

/** Why a read or a write was refused, in the admin's own words (D6). */
export function said(error: unknown): string {
  if (error instanceof AdminError) {
    return (
      SENTENCES[error.code ?? ''] ?? BY_STATUS[error.status] ?? error.message
    );
  }
  /* This tool's own refusals — no such address, a base that moved — already read as sentences. */
  if (error instanceof Error) return error.message;
  return said(toAdminError(error));
}
